<script setup lang="ts">
import { onClickOutside, useMagicKeys } from '@vueuse/core'
import { computed, nextTick, ref, unref, watch } from 'vue'
import { VButton, VIcon } from '@/modules/shared/components'
import VBadge from '@/modules/shared/components/v-badge.vue'
import { isMultiFilter, isRangeFilter } from './utils'
import type { FilterConfig, FilterOption, MultiFilterConfig, RangeFilterConfig } from './types'

type ResolvedFilter = FilterConfig & { options: FilterOption[] }

const props = defineProps<{ filters: FilterConfig[] }>()

const containerRef = ref<HTMLElement | null>(null)
const filtersListRef = ref<HTMLElement | null>(null)
const activeFilterOffset = ref(0)
const isOpen = ref(false)
const activeFilterKey = ref<string | null>(null)
const userModifiedKeys = new Set<string>()
const filterItemRefs = new Map<string, HTMLElement>()

defineExpose({
  markUserModified: (key: string) => userModifiedKeys.add(key),
  clearUserModified: () => {
    userModifiedKeys.clear()
    resolvedFilters.value.forEach((filter) => {
      if (!isMultiFilter(filter)) return
      if (!filter.assumeAllSelected) return
      filter.model.value = filter.options.map((option) => option.value)
    })
  },
})

const resolvedFilters = computed<ResolvedFilter[]>(() =>
  props.filters.map((filter) => {
    const options = isMultiFilter(filter) ? (unref(filter.options) ?? []) : []
    return {
      ...filter,
      type: filter.type ?? 'multi',
      options,
    }
  }),
)

const activeFilter = computed<ResolvedFilter | null>(() => {
  if (!activeFilterKey.value) return null
  const active = resolvedFilters.value.find((filter) => filter.key === activeFilterKey.value)
  return active ?? null
})

watch(
  resolvedFilters,
  (filters) => {
    if (!filters.length) {
      activeFilterKey.value = null
      return
    }

    if (activeFilterKey.value && !filters.some((filter) => filter.key === activeFilterKey.value)) {
      activeFilterKey.value = null
    }

    filters.forEach((filter) => {
      if (!isMultiFilter(filter)) return
      const shouldAssumeAll = filter.assumeAllSelected && !userModifiedKeys.has(filter.key)
      if (!shouldAssumeAll) return

      const optionValues = filter.options.map((option) => option.value)
      const modelValues = filter.model.value
      const differs =
        optionValues.length !== modelValues.length || optionValues.some((val, idx) => val !== modelValues[idx])

      if (differs) {
        filter.model.value = optionValues
      }
    })
  },
  { immediate: true, deep: true },
)

const close = () => {
  isOpen.value = false
  activeFilterKey.value = null
}

const setFilterRef = (key: string, el: HTMLElement | null) => {
  if (!el) {
    filterItemRefs.delete(key)
    return
  }

  filterItemRefs.set(key, el)
}

const toggle = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value === false) {
    activeFilterKey.value = null
  }
}

const selectFilter = (key: string) => {
  activeFilterKey.value = key
}

const updateActiveFilterOffset = () => {
  if (!activeFilterKey.value) {
    activeFilterOffset.value = 0
    return
  }

  const listEl = filtersListRef.value
  const activeEl = filterItemRefs.get(activeFilterKey.value)
  if (!listEl || !activeEl) return

  const listRect = listEl.getBoundingClientRect()
  const activeRect = activeEl.getBoundingClientRect()
  activeFilterOffset.value = activeRect.top - listRect.top
}

const optionIsSelected = (filter: ResolvedFilter | null, value: string) => {
  if (!filter || !isMultiFilter(filter)) return false
  return filter.model.value.includes(value)
}

const toggleOption = (filter: ResolvedFilter | null, value: string) => {
  if (!filter || !isMultiFilter(filter)) return
  const existing = new Set(filter.model.value)

  if (existing.has(value)) existing.delete(value)
  else existing.add(value)

  userModifiedKeys.add(filter.key)
  filter.model.value = Array.from(existing)
}

const toggleAll = (filter: ResolvedFilter | null) => {
  if (!filter || !isMultiFilter(filter)) return
  const allSelected = filter.model.value.length === filter.options.length

  userModifiedKeys.add(filter.key)
  filter.model.value = allSelected ? [] : filter.options.map((option) => option.value)
}

const defaultSummaryForMulti = (filter: MultiFilterConfig & { options: FilterOption[] }) => {
  if (!filter.model.value.length) return 'None selected'
  if (!filter.options.length) return `${filter.model.value.length} selected`
  if (filter.model.value.length === filter.options.length) return 'All selected'

  const selectedLabels = filter.options
    .filter((option) => filter.model.value.includes(option.value))
    .map((option) => option.label ?? option.text ?? option.value)

  return selectedLabels.length <= 2 ? selectedLabels.join(', ') : `${selectedLabels.length} selected`
}

const defaultSummaryForRange = (filter: RangeFilterConfig) => {
  const start = filter.range.start.value
  const end = filter.range.end.value

  if (!start && !end) return 'Any date'
  if (start && end) return `${start} → ${end}`
  if (start) return `From ${start}`
  return `Through ${end}`
}

const filterSummary = (filter: ResolvedFilter) => {
  const provided = 'summary' in filter ? unref(filter.summary as any) : ''
  if (provided) return provided

  if (isRangeFilter(filter)) return defaultSummaryForRange(filter)
  if (isMultiFilter(filter)) return defaultSummaryForMulti(filter as MultiFilterConfig & { options: FilterOption[] })
  return ''
}

type AppliedFilter = { key: string; label: string; summary: string; count: number }

const appliedFilters = computed<AppliedFilter[]>(
  () =>
    resolvedFilters.value
      .map((filter) => {
        if (isRangeFilter(filter)) {
          const hasValue = Boolean(filter.range.start.value) || Boolean(filter.range.end.value)
          return hasValue ? { key: filter.key, label: filter.label, summary: filterSummary(filter), count: 1 } : null
        }

        if (isMultiFilter(filter)) {
          const hasSelection = filter.model.value.length > 0
          const allSelected = filter.options.length > 0 && filter.model.value.length === filter.options.length
          if (!hasSelection || allSelected) return null

          return {
            key: filter.key,
            label: filter.label,
            summary: filterSummary(filter),
            count: filter.model.value.length,
          }
        }

        return null
      })
      .filter(Boolean) as AppliedFilter[],
)

const appliedCount = computed(() => appliedFilters.value.length)
const appliedByKey = computed<Record<string, number>>(() => {
  const map: Record<string, number> = {}
  appliedFilters.value.forEach((f) => {
    map[f.key] = f.count ?? 1
  })
  return map
})

const { escape } = useMagicKeys()

watch([activeFilterKey, resolvedFilters, isOpen], () => nextTick(updateActiveFilterOffset))

watch(escape, (pressed) => {
  if (pressed) close()
})

onClickOutside(containerRef, () => close())
</script>

<template>
  <div class="relative z-40" ref="containerRef">
    <VButton
      class=""
      :aria-expanded="isOpen"
      aria-label="Filters"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <div class="flex items-center gap-2">
        <div>Filters</div>
        <div class="flex items-center gap-1">
          <VBadge v-if="appliedCount" :value="appliedCount" size="xxs">{{ appliedCount }}</VBadge>
          <VIcon name="ChevronDown" class="h-4 w-4 text-gray-500" />
        </div>
      </div>
    </VButton>

    <Transition name="filters-dropdown-fade">
      <div v-if="isOpen && resolvedFilters.length" class="absolute left-0 mt-1 flex items-start">
        <div ref="filtersListRef" class="min-w-[220px] rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-700/20">
          <div
            v-for="filter in resolvedFilters"
            :key="filter.key"
            :ref="(el) => setFilterRef(filter.key, el)"
            class="flex cursor-pointer items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-gray-50"
            :class="{ 'bg-gray-50': filter.key === activeFilterKey }"
            @click="selectFilter(filter.key)"
          >
            <div class="text-sm text-gray-900">{{ filter.label }}</div>
            <div class="flex items-center gap-1">
              <VBadge v-if="appliedByKey[filter.key]" :value="appliedByKey[filter.key]" size="xxs">
                {{ appliedByKey[filter.key] }}
              </VBadge>
              <VIcon name="ChevronDown" class="h-4 w-4 -rotate-90 text-gray-500" />
            </div>
          </div>
        </div>

        <Transition name="filters-options-fade">
          <div v-if="activeFilter" class="relative -ml-1" :style="{ marginTop: `${activeFilterOffset}px` }">
            <div
              class="mt-1 max-h-[60vh] min-w-[240px] overflow-y-auto rounded-md bg-white pb-1 shadow-lg ring-1 ring-gray-700/20"
            >
              <template v-if="isMultiFilter(activeFilter)">
                <div class="border-b px-3 py-2 text-sm text-gray-700">
                  <VButton @click="toggleAll(activeFilter)" size="sm">{{
                    activeFilter.model.value.length === activeFilter.options.length ? 'Deselect all' : 'Select all'
                  }}</VButton>
                </div>
                <div
                  v-for="option in activeFilter.options"
                  :key="option.value"
                  class="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-50"
                  :class="{ 'cursor-not-allowed opacity-50': option.disabled }"
                  @click="option.disabled ? null : toggleOption(activeFilter, option.value)"
                >
                  <span class="flex h-4 w-4 items-center justify-center rounded border bg-white">
                    <VIcon
                      name="Check"
                      class="h-3 w-3"
                      :class="{ invisible: !optionIsSelected(activeFilter, option.value) }"
                    />
                  </span>
                  <span
                    class="min-w-0 truncate"
                    :class="{ 'text-gray-500': !optionIsSelected(activeFilter, option.value) }"
                  >
                    {{ option.label ?? option.text ?? option.value }}
                  </span>
                </div>
              </template>

              <template v-else-if="isRangeFilter(activeFilter)">
                <div class="space-y-3 px-4 py-3">
                  <div class="flex flex-col gap-1 text-sm">
                    <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">Start</label>
                    <input
                      type="date"
                      v-model="activeFilter.range.start.value"
                      class="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                  </div>
                  <div class="flex flex-col gap-1 text-sm">
                    <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">End</label>
                    <input
                      type="date"
                      v-model="activeFilter.range.end.value"
                      class="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.filters-dropdown-fade-enter-active,
.filters-dropdown-fade-leave-active {
  transition:
    opacity 120ms ease,
    transform 120ms ease;
}
.filters-dropdown-fade-enter-from,
.filters-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.filters-options-fade-enter-active,
.filters-options-fade-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}
.filters-options-fade-enter-from,
.filters-options-fade-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}
</style>
